import { Input } from "@nextui-org/react";
import React, { useContext, useEffect, useState, useRef } from "react";
import {
  Search,
  Filter,
  ArrowUpDown,
  Archive,
  Trash2,
  X,
  ChevronDown,
  LayoutGrid,
  LayoutList,
  Sparkles,
  Wand2,
  Send,
  MessageSquare,
  Loader2,
  Mic,
  StopCircle,
} from "lucide-react";
import { SearchFrondContext } from "Context/SearchFrondContext";
import { ArchiveContext } from "Context/ArchiveContext";
import { DeleteProjectsContext } from "Context/DeleteProjectsContext";
import ArchiveModal from "components/MyFrondsFuctions/ArchiveModal";
import DeleteModal from "components/MyFrondsFuctions/DeleteModal";
import { ViewProjectsContext } from "Context/ViewProjectsContext";
import SortModal from "components/FrondCard/SortModal";
import FilterModal from "components/FrondCard/FilterModal";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Fern from '../../Assets/fern2.png';
import qrCode from '../../Assets/landing/goals/qr2.jpeg';

const ProjectControl = ({ actions, page }) => {
  const navigate = useNavigate();
  const showFernAssistant = page === "my-fronds";
  
  const {
    myFrondsView,
    setMyFrondsView,
    sharedFrondsView,
    setSharedFrondsView,
    archiveFrondsView,
    setArchiveFrondsView,
    pageType,
  } = useContext(ViewProjectsContext);

  const { setSearchValue } = useContext(SearchFrondContext);

  const {
    openArchiveCheckbox,
    setOpenArchiveCheckbox,
    archiveProjects,
    setArchiveProjects,
  } = useContext(ArchiveContext);

  const {
    openDeleteCheckbox,
    setOpenDeleteCheckbox,
    deleteProjects,
    setDeleteProjects,
  } = useContext(DeleteProjectsContext);

  const [layout, setLayout] = useState("");
  const [aiMode, setAiMode] = useState("chat");
  const [chatMessage, setChatMessage] = useState("");
  const [generatePrompt, setGeneratePrompt] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [chatResponse, setChatResponse] = useState("");
  const [currentChatId, setCurrentChatId] = useState(null);
  const [hasPremiumAccess, setHasPremiumAccess] = useState(false);
  const [isCheckingAccess, setIsCheckingAccess] = useState(false);
  const [openArchiveModal, setOpenArchiveModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openSortModal, setOpenSortModal] = useState(false);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessingVoice, setIsProcessingVoice] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState("");
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(false);
  const [generatedData, setGeneratedData] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  const selectedCount = openArchiveCheckbox
    ? archiveProjects.length
    : openDeleteCheckbox
      ? deleteProjects.length
      : 0;

  let authToken = localStorage.getItem("token");

  const userHasPremiumPlan = (data = {}) => {
    const planSlug = data.plan_slug?.toLowerCase();
    const planName = data.plan_name?.toLowerCase();

    return planSlug === "premium" || planName === "premium";
  };

  useEffect(() => {
    if (pageType === "my-fronds") {
      setLayout(myFrondsView);
    } else if (pageType === "shared-fronds") {
      setLayout(sharedFrondsView);
    } else if (pageType === "archive-fronds") {
      setLayout(archiveFrondsView);
    }
  }, [pageType, myFrondsView, sharedFrondsView, archiveFrondsView]);

  const handleGridClick = () => {
    const newLayout = "grid";
    setLayout(newLayout);

    if (pageType === "my-fronds") {
      setMyFrondsView(newLayout);
    } else if (pageType === "shared-fronds") {
      setSharedFrondsView(newLayout);
    } else if (pageType === "archive-fronds") {
      setArchiveFrondsView(newLayout);
    }
  };

  const handleListClick = () => {
    const newLayout = "list";
    setLayout(newLayout);

    if (pageType === "my-fronds") {
      setMyFrondsView(newLayout);
    } else if (pageType === "shared-fronds") {
      setSharedFrondsView(newLayout);
    } else if (pageType === "archive-fronds") {
      setArchiveFrondsView(newLayout);
    }
  };

  const checkSubscription = async () => {
    if (!authToken) {
      setHasActiveSubscription(false);
      return false;
    }

    setCheckingSubscription(true);
    try {
      const response = await axios.get(
        '/billing/subscription-status',
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Accept': 'application/json',
          },
        }
      );
      
      const apiData = response.data?.data;
      const hasAccess = apiData?.has_active_subscription === true;
      
      setHasActiveSubscription(hasAccess);
      setSubscriptionData(apiData);
      setHasPremiumAccess(userHasPremiumPlan(apiData));
      return hasAccess;
    } catch (error) {
      console.error('Subscription check failed:', error);
      setHasActiveSubscription(false);
      setHasPremiumAccess(false);
      return false;
    } finally {
      setCheckingSubscription(false);
    }
  };

  const checkPremiumAccess = async () => {
    if (!authToken) {
      setHasPremiumAccess(false);
      return false;
    }

    setIsCheckingAccess(true);
    try {
      const response = await axios.get(
        '/billing/subscription-status',
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const apiData = response.data?.data || {};
      const hasActiveAccess = apiData.has_active_subscription === true;
      const hasPremiumAccess = userHasPremiumPlan(apiData);

      setHasActiveSubscription(hasActiveAccess);
      setSubscriptionData(apiData);
      setHasPremiumAccess(hasPremiumAccess);

      return hasPremiumAccess;
    } catch (error) {
      console.error('Failed to check premium access:', error);
      setHasPremiumAccess(false);
      return false;
    } finally {
      setIsCheckingAccess(false);
    }
  };

  useEffect(() => {
    if (showFernAssistant) {
      checkPremiumAccess();
    }
  }, [showFernAssistant]);

  // ============================================================
  // VOICE RECORDING FUNCTIONS - NO DEBUG
  // ============================================================

  const startRecording = async () => {
    try {
      if (mediaRecorderRef.current && isRecording) {
        stopRecording();
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        } 
      });
      
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        if (audioChunksRef.current.length === 0) {
          setIsRecording(false);
          return;
        }
        
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        sendVoiceToServer(audioBlob);
        
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
      };

      mediaRecorder.start(1000);
      setIsRecording(true);

    } catch (error) {
      console.error('Recording error:', error);
      toast.error('Could not access microphone. Please check permissions.', {
        position: "bottom-right",
        autoClose: 3000,
      });
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      try {
        mediaRecorderRef.current.stop();
        setIsRecording(false);
      } catch (error) {
        console.error('Error stopping recording:', error);
        setIsRecording(false);
      }
    } else {
      setIsRecording(false);
    }
  };

  const sendVoiceToServer = async (blob) => {
    setIsProcessingVoice(true);

    const formData = new FormData();
    const audioFile = new File([blob], 'voice-message.webm', { type: 'audio/webm' });
    formData.append('voice', audioFile);

    try {
      let endpoint;
      
      if (aiMode === 'chat') {
        endpoint = '/chat/voice';
      } else {
        endpoint = '/projects/generate-from-voice';
      }

      const response = await axios.post(endpoint, formData, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      if (aiMode === 'chat') {
        const data = response.data;
        
        const transcript = data.transcript || data.message || '';
        if (transcript) {
          setVoiceTranscript(transcript);
          setChatMessage(transcript);
        }
        
        const aiResponse = data.response || data.answer || data.reply || '';
        if (aiResponse) {
          setChatResponse(aiResponse);
          toast.success('Voice message processed!', {
            position: "bottom-right",
            autoClose: 2000,
          });
        }
        
        if (data.chat_id) {
          setCurrentChatId(data.chat_id);
        }
      } else {
        const data = response.data;
        
        const transcript = data.transcript || '';
        if (transcript) {
          setVoiceTranscript(transcript);
          setGeneratePrompt(transcript);
        }
        
        if (data.project) {
          const project = 
            data.project.frond ||
            data.project.project ||
            data.project;
          
          const projectId = project?.id;
          
          if (projectId) {
            toast.success(`✨ Project "${project?.title || 'Project'}" created from voice!`, {
              position: "bottom-right",
              autoClose: 3000,
            });
            
            setTimeout(() => {
              navigate(`/frond/about/${projectId}`, { 
                state: { 
                  focusOnMap: true,
                  newProjectLocation: {
                    lat: project?.latitude,
                    lng: project?.longitude,
                    name: project?.title || 'Project'
                  }
                }
              });
            }, 1000);
          }
        } else if (data.data && data.data.frond) {
          const project = data.data.frond;
          const projectId = project?.id;
          
          if (projectId) {
            toast.success(`✨ Project "${project?.title || 'Project'}" created from voice!`, {
              position: "bottom-right",
              autoClose: 3000,
            });
            
            setTimeout(() => {
              navigate(`/frond/about/${projectId}`, { 
                state: { 
                  focusOnMap: true,
                  newProjectLocation: {
                    lat: project?.latitude,
                    lng: project?.longitude,
                    name: project?.title || 'Project'
                  }
                }
              });
            }, 1000);
          }
        } else {
          toast.info('Voice processed but no project was created. Try again.', {
            position: "bottom-right",
            autoClose: 3000,
          });
        }
      }

      setTimeout(() => {
        audioChunksRef.current = [];
      }, 3000);

    } catch (error) {
      console.error('Voice send error:', error);
      
      const errorMsg = error.response?.data?.message || error.response?.data?.error || error.message;
      
      if (error.response?.status === 403) {
        toast.error('Premium subscription required for voice features.', {
          position: "bottom-right",
          autoClose: 3000,
        });
      } else if (error.response?.status === 422) {
        toast.error('Could not transcribe audio. Please try again.', {
          position: "bottom-right",
          autoClose: 3000,
        });
      } else if (error.response?.status === 429) {
        toast.error('Voice credits exhausted. Please wait for reset.', {
          position: "bottom-right",
          autoClose: 3000,
        });
      } else {
        toast.error(errorMsg || 'Failed to process voice message. Please try typing instead.', {
          position: "bottom-right",
          autoClose: 3000,
        });
      }
    } finally {
      setIsProcessingVoice(false);
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  // ============================================================
  // END VOICE RECORDING FUNCTIONS
  // ============================================================

  const handleGenerateProject = async () => {
    const hasSubscription = await checkSubscription();
    if (!hasSubscription) {
      setShowSubscriptionModal(true);
      toast.error('AI project generation requires an active subscription. Please subscribe to continue.', {
        position: "bottom-right",
        autoClose: 3000,
      });
      return;
    }

    if (!generatePrompt.trim() || generatePrompt.length < 5) {
      toast.warning('Please describe your project idea (minimum 5 characters)', {
        position: "bottom-right",
        autoClose: 2000,
      });
      return;
    }

    setIsSending(true);
    try {
      const response = await axios.post(
        '/projects/ai-create',
        {
          prompt: generatePrompt
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          }
        }
      );

      setGeneratedData(response.data);
      
      const projectData = response.data?.data;
      const project = projectData?.frond || projectData?.project;
      const projectId = project?.id;
      const planSlug = response.data?.subscription?.plan_slug || subscriptionData?.plan_slug;
      const isPremium = planSlug === 'premium';
      const unlimitedProjects = response.data?.subscription?.unlimited_projects === true || isPremium;
      
      const creditsUsed = response.data?.credits?.cost || 0;
      const creditsRemaining = response.data?.credits?.remaining || 0;
      const projectTitle = project?.title || projectData?.title || 'Project';
      
      if (projectId) {
        if (isPremium || unlimitedProjects) {
          toast.success(`✨ Project "${projectTitle}" created successfully!`, {
            position: "bottom-right",
            autoClose: 3000,
            icon: "🚀"
          });
        } else {
          toast.success(
            `✅ Project "${projectTitle}" created successfully!\n\n` +
            `📊 Credits used: ${creditsUsed}\n` +
            `💎 Credits remaining: ${creditsRemaining}\n\n` +
            `Your plan resets monthly. Upgrade to Premium for unlimited projects!`,
            {
              position: "bottom-right",
              autoClose: 5000,
              icon: "✨"
            }
          );
        }
        
        const locationData = {
          lat: project?.latitude || projectData?.latitude,
          lng: project?.longitude || projectData?.longitude,
          address: project?.address || projectData?.address,
          name: projectTitle
        };
        
        setGeneratePrompt("");
        
        navigate(`/frond/about/${projectId}`, { 
          state: { 
            focusOnMap: true,
            newProjectLocation: locationData,
            shouldFocusLocation: true
          }
        });
      } else {
        toast.success('Project created successfully!', {
          position: "bottom-right",
          autoClose: 2000,
        });
        setGeneratePrompt("");
      }
      
    } catch (error) {
      console.error('AI creation failed:', error);
      
      if (error.response?.status === 429) {
        const errorMessage = error.response?.data?.message || 'Insufficient credits. Please wait for your monthly reset or upgrade your plan.';
        toast.error(errorMessage, {
          position: "bottom-right",
          autoClose: 4000,
        });
      } else if (error.response?.status === 403) {
        toast.error('Subscription required. Please subscribe to use AI features.', {
          position: "bottom-right",
          autoClose: 3000,
        });
      } else {
        toast.error(error.response?.data?.message || 'Failed to create project', {
          position: "bottom-right",
          autoClose: 3000,
        });
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleSendMessage = async () => {
    if (aiMode === "chat") {
      if (!chatMessage.trim() || chatMessage.length < 3) {
        toast.warning('Please enter a message (minimum 3 characters)', {
          position: "bottom-right",
          autoClose: 2000,
        });
        return;
      }

      if (!hasPremiumAccess) {
        toast.error('Premium subscription required for AI chat. Please upgrade your plan.', {
          position: "bottom-right",
          autoClose: 4000,
        });
        return;
      }

      setIsSending(true);
      setChatResponse("");

      try {
        const response = await axios.post(
          '/message',
          {
            message: chatMessage,
            chat_id: currentChatId || null,
          },
          {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${authToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        const data = response.data;
        setChatResponse(data.response || "No response received");
        setCurrentChatId(data.chat_id || null);

        toast.success('Response received!', {
          position: "bottom-right",
          autoClose: 2000,
        });

        setChatMessage("");
      } catch (error) {
        console.error('Chat error:', error);
        
        const errorMessage = error.response?.data?.message || 'Failed to send message';
        
        if (error.response?.status === 403) {
          toast.error('Premium subscription required. Please upgrade your plan.', {
            position: "bottom-right",
            autoClose: 3000,
          });
        } else if (error.response?.status === 422) {
          toast.error('Invalid message format. Please try again.', {
            position: "bottom-right",
            autoClose: 2000,
          });
        } else if (error.response?.status === 503) {
          toast.error('AI service is currently unavailable. Please try again later.', {
            position: "bottom-right",
            autoClose: 3000,
          });
        } else {
          toast.error(errorMessage, {
            position: "bottom-right",
            autoClose: 3000,
          });
        }
      } finally {
        setIsSending(false);
      }
    } else {
      await handleGenerateProject();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const projectIdeas = [
    { 
      label: "🏝️ Beach Vacation", 
      prompt: "Plan a 7-day all-inclusive beach vacation to Maldives for 4 people with a budget of $8000, including flights, water villas, snorkeling, and sunset cruises"
    },
    { 
      label: "🏔️ Mountain Adventure", 
      prompt: "Create a 5-day hiking adventure to the Swiss Alps for 3 friends, including train tickets, mountain huts, guided glacier walks, and a paragliding experience"
    },
    { 
      label: "🏙️ City Break", 
      prompt: "Plan a 4-day trip to Tokyo for 2 people in October, covering flights, capsule hotel, sushi-making class, Mount Fuji day trip, and a night in Shinjuku"
    },
    { 
      label: "🌿 Eco-Tourism", 
      prompt: "Design a 6-day eco-friendly trip to Costa Rica for a family of 4, including rainforest lodges, turtle watching, zip-lining, and local community experiences"
    },
    { 
      label: "🎒 Backpacking", 
      prompt: "Create a 3-week Southeast Asia backpacking itinerary for 2 travelers across Thailand, Vietnam, and Cambodia with a daily budget of $50 per person"
    },
    { 
      label: "🚗 Road Trip", 
      prompt: "Plan a 10-day California road trip from San Francisco to Los Angeles for 2 couples, including a convertible rental, coastal stays, wine tasting, and Hollywood tours"
    },
  ];

  const chatSuggestions = [
    { label: "🇯🇵 7-day Japan itinerary", icon: "🇯🇵" },
    { label: "🏖️ Beach packing tips", icon: "🏖️" },
    { label: "🍜 Foodie travel guide", icon: "🍜" },
    { label: "✈️ Solo travel safety", icon: "✈️" },
    { label: "🏔️ Adventure activities", icon: "🏔️" },
  ];

  return (
    <>
      <div className="bg-[#fdf6f5] rounded-4xl p-4 md:p-6 mb-5 border border-[#e1bec2]/20">
        {showFernAssistant && (
        <div className="mt-2">
          <div className="bg-gradient-to-br from-[#fdf9f5] via-[#facece] to-[#f9eaec] rounded-2xl p-6 md:p-8 relative overflow-hidden clay-card min-h-[200px] flex flex-col justify-center items-center text-center border border-[#e1bec2]/60">
            <div className="absolute inset-0 opacity-40 pointer-events-none">
              <div className="absolute top-0 left-0 w-64 h-64 bg-[#ffb2be]/20 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 right-0 w-64 h-64 bg-[#e0e0ff]/20 rounded-full blur-3xl"></div>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-mainGradiant rounded-full blur-3xl"></div>
            </div>

            {aiMode === "chat" && (
              <div className="absolute top-4 right-4 z-20">
                <span className={`text-xs font-medium px-3 py-1 rounded-full ${
                  hasPremiumAccess 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-gray-100 text-gray-500'
                }`}>
                  {hasPremiumAccess ? '🟢 Premium' : '🔒 Premium Required'}
                </span>
              </div>
            )}

            <div className="relative z-10 w-full max-w-3xl">
              {aiMode === "chat" ? (
                <>
                  <div className="flex justify-center">
                    <h1 className="font-display-lg text-display-lg-mobile md:text-4xl font-extrabold text-blueTimeline mb-2 leading-tight">
                      Chat with Fern AI 
                    </h1>
                    <span><img src={Fern} alt="AI Create Project" className="w-15 h-10" /></span>
                  </div>
                  <p className="text-gradient-to-br from-[#ea3c70] to-[#e82c07] text-sm md:text-base mb-4">
                    Your personal travel assistant - ask anything about your trip!
                  </p>
                </>
              ) : (
                <>
                  <h1 className="font-display-lg text-display-lg-mobile md:text-4xl font-extrabold text-blueTimeline mb-2 leading-tight">
                    Where shall we go together? 
                  </h1>
                  <p className="text-gradient-to-br from-[#ea3c70] to-[#e82c07] text-sm md:text-base mb-4">
                    Let Fern AI help you create a complete project plan in seconds
                  </p>
                </>
              )}

              <div className="flex justify-center gap-2 mb-4">
                <button
                  onClick={() => setAiMode("chat")}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 ${
                    aiMode === "chat"
                      ? "bg-gradient-to-r from-[#ea3c70] to-[#e82c07] text-white shadow-[4px_4px_12px_rgba(216,49,98,0.3)] hover:shadow-[6px_6px_16px_rgba(216,49,98,0.4)]"
                      : "bg-white/80 backdrop-blur-sm text-[#594044] shadow-[inset_4px_4px_8px_#e5e2de,inset_-4px_-4px_8px_#ffffff] hover:shadow-[inset_6px_6px_12px_#e5e2de,inset_-6px_-6px_12px_#ffffff]"
                  }`}
                >
                  <MessageSquare className="w-4 h-4" />
                  Chat with Fern
                </button>
                <button
                  onClick={() => setAiMode("generate")}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 ${
                    aiMode === "generate"
                      ? "bg-gradient-to-r from-[#ea3c70] to-[#e82c07] text-white shadow-[4px_4px_12px_rgba(216,49,98,0.3)] hover:shadow-[6px_6px_16px_rgba(216,49,98,0.4)]"
                      : "bg-white/80 backdrop-blur-sm text-[#594044] shadow-[inset_4px_4px_8px_#e5e2de,inset_-4px_-4px_8px_#ffffff] hover:shadow-[inset_6px_6px_12px_#e5e2de,inset_-6px_-6px_12px_#ffffff]"
                  }`}
                >
                  <Wand2 className="w-4 h-4" />
                  Generate a Project
                </button>
              </div>

              <div className="flex flex-col md:flex-row gap-4 w-full bg-white/40 backdrop-blur-md p-3 rounded-4xl border border-white/60 shadow-[inset_4px_4px_8px_#e5e2de,inset_-4px_-4px_8px_#ffffff]">
                <div className="flex-grow relative">
                  {aiMode === "chat" ? (
                    <textarea
                      value={chatMessage}
                      onChange={(e) => setChatMessage(e.target.value)}
                      onKeyDown={handleKeyDown}
                      disabled={!hasPremiumAccess || isSending}
                      className="w-full bg-white/60 border-none rounded-4xl p-4 shadow-[inset_4px_4px_8px_#e5e2de,inset_-4px_-4px_8px_#ffffff] focus:ring-2 ring-[#d83162] min-h-[80px] text-[#211a1b] placeholder:text-[#594044]/50 text-sm resize-none disabled:opacity-60"
                      placeholder={hasPremiumAccess ? "Ask Fern AI anything about your trip..." : "Premium subscription required to chat with Fern AI"}
                    />
                  ) : (
                    <textarea
                      value={generatePrompt}
                      onChange={(e) => setGeneratePrompt(e.target.value)}
                      onKeyDown={handleKeyDown}
                      disabled={isSending}
                      className="w-full bg-white/60 border-none rounded-4xl p-4 shadow-[inset_4px_4px_8px_#e5e2de,inset_-4px_-4px_8px_#ffffff] focus:ring-2 ring-[#d83162] min-h-[80px] text-[#211a1b] placeholder:text-[#594044]/50 text-sm resize-none disabled:opacity-60"
                      placeholder="Describe your dream group trip to the Swiss Alps or a weekend in Tokyo..."
                    />
                  )}
                  
                  <button
                    onClick={toggleRecording}
                    disabled={isProcessingVoice || isSending}
                    className={`absolute bottom-4 right-4 p-2 rounded-full transition-all duration-300 ${
                      isRecording 
                        ? 'bg-red-500 text-white animate-pulse shadow-[0_0_20px_rgba(220,38,38,0.5)]' 
                        : isProcessingVoice
                        ? 'bg-yellow-500 text-white'
                        : 'bg-[#f9eaec] text-[#594044] hover:bg-[#ea3c70] hover:text-white'
                    }`}
                    title={isRecording ? "Stop recording" : "Start voice recording"}
                  >
                    {isRecording ? (
                      <StopCircle className="w-5 h-5" />
                    ) : isProcessingVoice ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Mic className="w-5 h-5" />
                    )}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={handleSendMessage}
                  disabled={isSending || (aiMode === "chat" && !hasPremiumAccess)}
                  className="group relative overflow-hidden rounded-full bg-gradient-to-br from-[#ff7a96] via-[#ea3c70] to-[#e82c07] px-8 py-4 text-lg font-extrabold text-white shadow-[0_14px_35px_rgba(216,49,98,0.34),0_6px_16px_rgba(0,0,0,0.16)] backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:scale-[1.03] hover:shadow-[0_20px_45px_rgba(216,49,98,0.42),0_10px_24px_rgba(0,0,0,0.22)] active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100 md:px-12 animate-float"
                >
                  <span className="absolute inset-0 rounded-full bg-orange-600/20 opacity-0 transition-opacity duration-300 group-hover:opacity-100"></span>
                  <span className="absolute inset-x-2 top-1 h-1/3 rounded-full bg-white/20 blur-sm"></span>
                  <span className="absolute left-1/2 top-1/2 h-12 w-12 -translate-x-1/2 -translate-y-1/2 rounded-full bg-black/20 blur-xl transition-all duration-500 group-hover:h-16 group-hover:w-16"></span>
                  <span className="absolute inset-0 rounded-full"></span>
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {isSending ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>{aiMode === "chat" ? "Thinking..." : "Generating..."}</span>
                      </>
                    ) : (
                      <>
                        <span>{aiMode === "chat" ? "Send" : "Generate"}</span>
                        {aiMode === "chat" ? (
                          <Send className="h-5 w-5 fill-current" />
                        ) : (
                          <Sparkles className="h-5 w-5 fill-current" />
                        )}
                      </>
                    )}
                  </span>
                </button>
              </div>

              {isRecording && (
                <div className="mt-3 flex items-center justify-center gap-2 animate-pulse">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span className="text-sm font-medium text-red-500">What's on your mind?...</span>
                  <span className="text-xs text-gray-400">(click mic to stop)</span>
                </div>
              )}

              {isProcessingVoice && (
                <div className="mt-3 flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-[#ea3c70]" />
                  <span className="text-sm font-medium text-[#594044]">Generating...</span>
                </div>
              )}

              {aiMode === "chat" && chatResponse && (
                <div className="mt-4 p-4 bg-white/80 backdrop-blur-sm rounded-2xl shadow-[inset_4px_4px_8px_#e5e2de,inset_-4px_-4px_8px_#ffffff] text-left border border-royalBlue/10">
                  <div className="flex items-start gap-2">
                    <span className="text-lg">🤖</span>
                    <div>
                      <p className="text-xs font-semibold text-[#594044]/60 mb-1">Fern AI:</p>
                      <p className="text-sm text-[#211a1b] whitespace-pre-wrap">{chatResponse}</p>
                    </div>
                  </div>
                </div>
              )}

              {aiMode === "generate" && (
                <div className="mt-4">
                  <p className="text-xs font-medium text-[#594044]/70 mb-2 text-center">💡 Try one of these project ideas:</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {projectIdeas.map((idea, index) => (
                      <button
                        key={index}
                        onClick={() => setGeneratePrompt(idea.prompt)}
                        className="bg-white/80 backdrop-blur-sm text-[#594044] px-3 py-1.5 rounded-full text-xs font-medium hover:bg-[#f9eaec] hover:text-[#b50f4a] transition-all duration-200 border border-[#e1bec2]/30 shadow-[inset_2px_2px_4px_#e5e2de,inset_-2px_-2px_4px_#ffffff] hover:shadow-[inset_4px_4px_8px_#e5e2de,inset_-4px_-4px_8px_#ffffff]"
                      >
                        {idea.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {aiMode === "chat" && hasPremiumAccess && (
                <div className="mt-4 flex flex-wrap justify-center gap-2">
                  <span className="text-xs font-medium text-[#594044]/70 mr-1">Ask me about:</span>
                  {chatSuggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => setChatMessage(suggestion.label.replace(/^.{1,2}\s/, ''))}
                      className="bg-[#f9eaec] text-[#594044] px-3 py-1 rounded-full text-xs font-medium hover:bg-[#eedfe1] transition-colors duration-200"
                    >
                      {suggestion.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        <div className={`flex flex-col gap-4 ${showFernAssistant ? "mt-4" : ""}`}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex-1 flex items-center gap-3">
              <div className="relative flex-1 max-w-sm">
                <Input
                  onInput={(e) => {
                    setSearchValue(e.target.value);
                  }}
                  type="text"
                  placeholder="Search your fronds..."
                  startContent={<Search className="w-4 h-4 text-[#594044]/60" />}
                  classNames={{
                    base: "w-full",
                    input: "border-none p-0 text-sm",
                    inputWrapper:
                      "bg-white/80 backdrop-blur-sm rounded-full px-4 py-2.5 shadow-[inset_4px_4px_8px_#e5e2de,inset_-4px_-4px_8px_#ffffff] border-none transition-all duration-300 focus-within:shadow-[inset_6px_6px_12px_#e5e2de,inset_-6px_-6px_12px_#ffffff]",
                    mainWrapper: "w-full",
                  }}
                />
              </div>

              <div className="hidden md:flex items-center gap-2">
                <button
                  onClick={() => setOpenFilterModal(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm shadow-[inset_4px_4px_8px_#e5e2de,inset_-4px_-4px_8px_#ffffff] hover:shadow-[inset_6px_6px_12px_#e5e2de,inset_-6px_-6px_12px_#ffffff] transition-all duration-300 text-[#594044] hover:text-[#b50f4a]"
                >
                  <Filter className="w-4 h-4" />
                  <span className="text-sm font-medium">Filter</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
                <button
                  onClick={() => setOpenSortModal(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm shadow-[inset_4px_4px_8px_#e5e2de,inset_-4px_-4px_8px_#ffffff] hover:shadow-[inset_6px_6px_12px_#e5e2de,inset_-6px_-6px_12px_#ffffff] transition-all duration-300 text-[#594044] hover:text-[#b50f4a]"
                >
                  <ArrowUpDown className="w-4 h-4" />
                  <span className="text-sm font-medium">Sort</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1 bg-white/80 backdrop-blur-sm rounded-full p-1 shadow-[inset_4px_4px_8px_#e5e2de,inset_-4px_-4px_8px_#ffffff]">
                <button
                  type="button"
                  onClick={handleGridClick}
                  className={`w-9 h-9 flex items-center justify-center rounded-full transition-all duration-300 ${
                    layout === "grid"
                      ? "bg-[#f76d6d] text-white shadow-[4px_4px_8px_#d83162,inset_-2px_-2px_4px_#b50f4a]"
                      : "text-[#594044] hover:bg-[#f9eaec]"
                  }`}
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={handleListClick}
                  className={`w-9 h-9 flex items-center justify-center rounded-full transition-all duration-300 ${
                    layout === "list"
                      ? "bg-[#ec4509] text-white shadow-[4px_4px_8px_#d83162,inset_-2px_-2px_4px_#b50f4a]"
                      : "text-[#594044] hover:bg-[#f9eaec]"
                  }`}
                >
                  <LayoutList className="w-4 h-4" />
                </button>
              </div>

              <div className="flex md:hidden items-center gap-2">
                <button
                  onClick={() => setOpenFilterModal(true)}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-white/80 backdrop-blur-sm shadow-[inset_4px_4px_8px_#e5e2de,inset_-4px_-4px_8px_#ffffff] hover:shadow-[inset_6px_6px_12px_#e5e2de,inset_-6px_-6px_12px_#ffffff] transition-all duration-300 text-[#594044]"
                >
                  <Filter className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setOpenSortModal(true)}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-white/80 backdrop-blur-sm shadow-[inset_4px_4px_8px_#e5e2de,inset_-4px_-4px_8px_#ffffff] hover:shadow-[inset_6px_6px_12px_#e5e2de,inset_-6px_-6px_12px_#ffffff] transition-all duration-300 text-[#594044]"
                >
                  <ArrowUpDown className="w-4 h-4" />
                </button>
              </div>

              {actions && (
                <div className="flex items-center gap-2">
                  {openArchiveCheckbox || openDeleteCheckbox ? (
                    <>
                      {selectedCount > 0 && (
                        <span className="text-xs font-medium text-[#594044] bg-[#f9eaec] px-3 py-1 rounded-full">
                          {selectedCount} selected
                        </span>
                      )}
                      <button
                        onClick={() => {
                          if (openArchiveCheckbox && archiveProjects.length > 0) {
                            setOpenArchiveModal(true);
                            setOpenArchiveCheckbox(false);
                          } else if (openDeleteCheckbox && deleteProjects.length > 0) {
                            setOpenDeleteModal(true);
                            setOpenDeleteCheckbox(false);
                          }
                        }}
                        disabled={selectedCount === 0}
                        className={`px-4 py-2 rounded-full font-medium text-sm transition-all duration-300 ${
                          selectedCount > 0
                            ? openArchiveCheckbox
                              ? "bg-[#da3600] text-white shadow-[4px_4px_12px_rgba(218,54,0,0.3)] hover:shadow-[6px_6px_16px_rgba(218,54,0,0.4)] hover:scale-105"
                              : "bg-red-500 text-white shadow-[4px_4px_12px_rgba(220,38,38,0.3)] hover:shadow-[6px_6px_16px_rgba(220,38,38,0.4)] hover:scale-105"
                            : "bg-gray-200 text-gray-400 cursor-not-allowed"
                        }`}
                      >
                        {openArchiveCheckbox ? (
                          <span className="flex items-center gap-2">
                            <Archive className="w-4 h-4" />
                            Archive
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </span>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setOpenArchiveCheckbox(false);
                          setOpenDeleteCheckbox(false);
                          setArchiveProjects([]);
                          setDeleteProjects([]);
                        }}
                        className="w-9 h-9 flex items-center justify-center rounded-full bg-white/80 backdrop-blur-sm shadow-[inset_4px_4px_8px_#e5e2de,inset_-4px_-4px_8px_#ffffff] hover:shadow-[inset_6px_6px_12px_#e5e2de,inset_-6px_-6px_12px_#ffffff] transition-all duration-300 text-[#594044] hover:text-[#b50f4a]"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    actions.map((item, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => {
                          if (item === "Archive") {
                            setOpenArchiveCheckbox(true);
                          } else if (item === "Delete") {
                            setOpenDeleteCheckbox(true);
                          }
                        }}
                        className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm shadow-[inset_4px_4px_8px_#e5e2de,inset_-4px_-4px_8px_#ffffff] hover:shadow-[inset_6px_6px_12px_#e5e2de,inset_-6px_-6px_12px_#ffffff] transition-all duration-300 text-[#594044] hover:text-[#b50f4a]"
                      >
                        {item === "Archive" ? (
                          <Archive className="w-4 h-4" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                        <span className="text-sm font-medium hidden sm:inline">{item}</span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {openArchiveModal && (
        <ArchiveModal
          isOpen={openArchiveModal}
          onClose={() => {
            setOpenArchiveModal(false);
          }}
          projects={archiveProjects}
        />
      )}
      {openDeleteModal && (
        <DeleteModal
          isOpen={openDeleteModal}
          onClose={() => {
            setOpenDeleteModal(false);
          }}
          projects={deleteProjects}
        />
      )}
      {openSortModal && (
        <SortModal
          isOpen={openSortModal}
          onClose={() => {
            setOpenSortModal(false);
          }}
          page={page}
        />
      )}
      {openFilterModal && (
        <FilterModal
          isOpen={openFilterModal}
          onClose={() => {
            setOpenFilterModal(false);
          }}
          page={page}
        />
      )}

      {showSubscriptionModal && (
        <div className="fixed inset-0 z-[99999] flex items-start justify-center overflow-y-auto bg-black/60 px-4 py-6 backdrop-blur-sm sm:py-10">
          <div className="w-full max-w-xl max-h-[calc(100vh-3rem)] overflow-y-auto rounded-[28px] bg-white shadow-2xl animate-fadeIn">
            <div className="bg-gradient-to-r from-orange-500 to-pink-500 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                    <span className="text-xl">📱</span>
                  </div>
                  <h3 className="text-xl font-bold text-white">Subscription Required</h3>
                </div>
                <button
                  className="text-3xl text-white/80 transition-colors hover:text-white"
                  onClick={() => setShowSubscriptionModal(false)}
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6 text-center">
              <h3 className="mb-2 text-xl font-bold text-gray-800">
                AI Project Generation Requires a Subscription
              </h3>

              <p className="mb-6 text-sm text-gray-600">
                To generate projects with Fern AI, you need an active subscription. Subscribe through our mobile app to get started.
              </p>

              <div className="flex flex-col gap-4 rounded-xl bg-gradient-to-r from-purple-50 to-pink-50 p-4 md:flex-row">
                <div className="mb-2 flex-1 text-left">
                  <p className="mb-2 flex items-center gap-2 font-semibold text-gray-700">
                    <span>🎯</span> What you get:
                  </p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center gap-2">
                      <span className="text-green-500">✓</span> AI project creation
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-500">✓</span> AI task generation
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-500">✓</span> AI image generation
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-500">✓</span> Chat with Fern AI (Premium Plan)
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-500">✓</span> Priority support
                    </li>
                  </ul>
                </div>

                <div className="flex-1">
                  <p className="mb-3 mt-1 flex items-center justify-center gap-2 text-sm font-semibold text-gray-700">
                    Scan to download the app
                  </p>
                  <div className="flex justify-center">
                    <div className="inline-block rounded-2xl bg-white p-2 shadow-lg">
                      <img src={qrCode} alt="Download Fern App" className="h-38 w-38 object-contain" />
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Available on iOS and Android</p>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs text-gray-400">
                  Already have a subscription? Make sure you're logged in with the same account.
                </p>
              </div>
            </div>
            <div className="mb-5 rounded-2xl border border-dashed border-pink-200 bg-white/80 p-4 text-center shadow-sm">
              <p className="mb-2 text-sm font-semibold text-gray-700">
                Or 
              </p>
              <p className="mb-3 text-xs text-gray-500">
                start building manually.
              </p>
              <button
                type="button"
                onClick={() => {
                  setShowSubscriptionModal(false);
                  navigate('/create-frond');
                }}
                className="rounded-full bg-gradient-to-r from-[#ea3c70] to-[#e82c07] px-5 py-2 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:scale-[1.02] hover:shadow-lg"
              >
                Create a Frond 
              </button>
            </div>

            <div className="flex justify-center border-t border-gray-100 bg-gray-50 px-6 py-1">
              <button
                className="rounded-lg px-6 py-2 font-medium text-gray-600 transition-all duration-200 hover:bg-gray-100 hover:text-gray-800"
                onClick={() => setShowSubscriptionModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectControl;
